/**
 * Add someone to the project by work email, with a role up to the caller's own
 * (owners and admins only; a member adds reviewers by sharing a queue). Nobody
 * is emailed, so after adding, the form offers the project link to share.
 */

import { useState } from "react";
import { Link2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { errorText } from "@/components/ui/states";
import { useAddMember, useProject } from "@/features/projects/hooks";
import { ROLE_LABEL, roleOptions, rolesUpTo } from "@/features/projects/roles";
import type { ProjectRole } from "@/features/projects/types";
import { projectHref } from "@/lib/project-url";
import { useUiStore } from "@/stores/ui-store";

const labelClass = "text-[13px] font-semibold";

function CopyProjectLink() {
  const { slug } = useProject();
  const notify = useUiStore((s) => s.notify);
  if (!slug) return null;
  const copy = () =>
    void navigator.clipboard
      ?.writeText(`${window.location.origin}${projectHref(slug)}`)
      .then(() => notify("Project link copied."));
  return (
    <Button size="sm" onClick={copy}>
      <Link2 size={13} strokeWidth={2} aria-hidden />
      Copy project link
    </Button>
  );
}

export function AddMemberForm({ myRole }: { myRole: ProjectRole }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ProjectRole>("member");
  const [added, setAdded] = useState<string | null>(null);
  const add = useAddMember();
  const notify = useUiStore((s) => s.notify);
  const options = roleOptions(rolesUpTo(myRole));
  const submit = () =>
    add.mutate(
      { email: email.trim(), role },
      {
        onSuccess: (m) => {
          const message = `Added ${m.email} as ${ROLE_LABEL[m.role]}.${
            m.pending ? " They'll see it after they sign in." : ""
          }`;
          notify(message);
          setAdded(message);
          setEmail("");
        },
        onError: (e) => notify(errorText(e, "Could not add them."), "error"),
      },
    );
  return (
    <div className="flex flex-col gap-2 border-b border-line-soft px-4 py-3">
      <form className="flex items-end gap-2" onSubmit={(e) => (e.preventDefault(), submit())}>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <label htmlFor="add-member-email" className={labelClass}>
            Email
          </label>
          <input
            id="add-member-email"
            type="email"
            placeholder="name@qure.ai"
            className="inp"
            value={email}
            onChange={(e) => (setEmail(e.target.value), setAdded(null))}
          />
        </div>
        <div className="flex w-40 flex-col gap-1.5">
          <label htmlFor="add-member-role" className={labelClass}>
            Role
          </label>
          <Select
            id="add-member-role"
            value={role}
            onChange={(r) => setRole(r as ProjectRole)}
            options={options}
          />
        </div>
        <Button type="submit" variant="primary" disabled={!email.trim() || add.isPending}>
          <UserPlus size={14} strokeWidth={2} aria-hidden />
          Add member
        </Button>
      </form>
      {added && (
        <div role="status" className="flex items-center gap-3 text-[13px] text-muted">
          <span className="flex-1">{added} Share the link with them.</span>
          <CopyProjectLink />
        </div>
      )}
    </div>
  );
}
